import type {
  ChangeEntry,
  ChangeStatus,
  StorageAdapter,
  SyncPushRequest,
} from "./types"

/**
 * Exported so callers that must read the shield (`getActiveEntityIds`) inside
 * their own storage transaction can put this table in the transaction scope —
 * Dexie rejects a read against a table outside the active transaction.
 */
export const CHANGE_QUEUE_TABLE = "_changeQueue"
const MAX_BACKOFF_MS = 30_000
const BASE_BACKOFF_MS = 1_000

function generateEntryId(): string {
  return `cq_${crypto.randomUUID()}`
}

export class ChangeQueue {
  constructor(private storage: StorageAdapter) {}

  async enqueue<T>(
    resource: string,
    change: SyncPushRequest<T>["changes"][number]
  ): Promise<ChangeEntry<T>> {
    const entry: ChangeEntry<T> = {
      id: generateEntryId(),
      resource,
      change,
      status: "pending",
      retryCount: 0,
      createdAt: Date.now(),
    }
    await this.storage.put(CHANGE_QUEUE_TABLE, entry)
    return entry
  }

  async dequeueNext(resource?: string): Promise<ChangeEntry | null> {
    const filter = resource
      ? { resource, status: "pending" as ChangeStatus, orderBy: "createdAt" }
      : { status: "pending" as ChangeStatus, orderBy: "createdAt" }
    const pending = await this.storage.getAll<ChangeEntry>(
      CHANGE_QUEUE_TABLE,
      filter
    )
    if (pending.length === 0) return null

    const entry = pending[0]!
    const updated: ChangeEntry = { ...entry, status: "syncing" }
    await this.storage.put(CHANGE_QUEUE_TABLE, updated)
    return updated
  }

  async markSynced(id: string): Promise<void> {
    await this.storage.delete(CHANGE_QUEUE_TABLE, id)
  }

  async markFailed(id: string): Promise<void> {
    const entry = await this.storage.get<ChangeEntry>(CHANGE_QUEUE_TABLE, id)
    if (!entry) return
    const updated: ChangeEntry = {
      ...entry,
      status: "failed",
      retryCount: entry.retryCount + 1,
    }
    await this.storage.put(CHANGE_QUEUE_TABLE, updated)
  }

  async markRetryable(id: string): Promise<void> {
    const entry = await this.storage.get<ChangeEntry>(CHANGE_QUEUE_TABLE, id)
    if (!entry) return
    const updated: ChangeEntry = { ...entry, status: "pending" }
    await this.storage.put(CHANGE_QUEUE_TABLE, updated)
  }

  async getPending(resource?: string): Promise<ChangeEntry[]> {
    const all = await this.storage.getAll<ChangeEntry>(CHANGE_QUEUE_TABLE)
    return all.filter(
      (e) =>
        (e.status === "pending" || e.status === "failed") &&
        (!resource || e.resource === resource)
    )
  }

  /**
   * Entity ids for a resource that have an IN-FLIGHT local change — `pending`
   * (queued) or `syncing` (being pushed). The SyncEngine uses this to shield
   * those rows so an older server echo can't clobber the user's optimistic
   * write before it is acknowledged. `failed` is deliberately excluded: a change
   * that never reached the server must not keep winning over server truth.
   */
  async getActiveEntityIds(resource: string): Promise<Set<string>> {
    const all = await this.storage.getAll<ChangeEntry>(CHANGE_QUEUE_TABLE)
    const ids = new Set<string>()
    for (const e of all) {
      if (e.resource !== resource) continue
      if (e.status !== "pending" && e.status !== "syncing") continue
      const entityId = (e.change as { id?: unknown }).id
      if (typeof entityId === "string") ids.add(entityId)
    }
    return ids
  }

  async getAll(): Promise<ChangeEntry[]> {
    return this.storage.getAll<ChangeEntry>(CHANGE_QUEUE_TABLE)
  }

  async clear(): Promise<void> {
    await this.storage.clear(CHANGE_QUEUE_TABLE)
  }

  /**
   * Drop every queued entry for one resource. Used by the scope-invalidation
   * wipe (STK-1238): entries left behind would reference wiped rows — they'd
   * shield ghost ids against the re-pull and re-push edits against rows that
   * may have left the viewer's scope. The wipe flushes the queue first
   * (best-effort), so anything still here is either failed or unpushable.
   */
  async clearResource(resource: string): Promise<void> {
    const all = await this.storage.getAll<ChangeEntry>(CHANGE_QUEUE_TABLE)
    for (const entry of all) {
      if (entry.resource === resource) {
        await this.storage.delete(CHANGE_QUEUE_TABLE, entry.id)
      }
    }
  }

  getBackoffMs(retryCount: number): number {
    return Math.min(BASE_BACKOFF_MS * 2 ** retryCount, MAX_BACKOFF_MS)
  }
}
